import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'

import {
    clearBookingState,
    selectBookingOriginalOrder,
    selectIsCompanyBookingForm,
} from 'features/Booking/model/slices'
import { getBookingRange } from 'features/OrderPreview/ui/OrderTotalPricePreview/utils'
import { useFillStore } from 'features/Booking/model/hooks/useFillStore'
import {
    PaymentMethodLabels,
    PaymentMethodName,
    RefundBooking,
    RefundPaymentRequest,
} from 'entities/payments/model'
import { PaymentApi } from 'entities/payments/api'
import { AutocompleteOption } from 'shared/components/Autocomplete'
import { closeSidebar } from 'shared/redux/slice/sidebar'
import { OrderBooking } from 'entities/orders/model'

export const refundWayOptions: AutocompleteOption[] = [
    {
        label: PaymentMethodLabels.CASH,
        value: PaymentMethodName.Cash,
    },
    {
        label: PaymentMethodLabels.ONLINE_CLOUDPAYMENTS,
        value: PaymentMethodName.OnlineCloudPayments,
    },
    {
        label: PaymentMethodLabels.ONLINE_YOOKASSA,
        value: PaymentMethodName.OnlineYookassa,
    },
    {
        label: PaymentMethodLabels.CERTIFICATE,
        value: PaymentMethodName.Certificate,
    },
]

export const companyRefundWayOptions: AutocompleteOption[] = [
    ...refundWayOptions,
    {
        label: PaymentMethodLabels.ACCOUNT,
        value: PaymentMethodName.Account,
    },
]

const getBookingRangeDays = (booking: OrderBooking) =>
    getBookingRange(booking)?.diff ?? 1

export const useOrderRefundController = () => {
    const dispatch = useDispatch()
    const originalOrder = useSelector(selectBookingOriginalOrder)
    const isCompany = useSelector(selectIsCompanyBookingForm)
    const updateBookings = useFillStore()

    const [total, setTotal] = useState(0)
    const [refundComment, setRefundComment] = useState('')

    const [currentRefundWay, setCurrentRefundWay] =
        useState<AutocompleteOption | null>(null)
    const [selectedBookings, setSelectedBookings] = useState<RefundBooking[]>(
        [],
    )
    const [isAreaDisabled, setIsAreaDisabled] = useState<{
        [key: number]: boolean
    }>({})
    const [isNomenclatureDisabled, setIsNomenclatureDisabled] = useState<{
        [key: string]: boolean
    }>({})
    const totalAmountPaid = originalOrder?.totalAmountPaid ?? 0
    const availableMoney = totalAmountPaid - total

    const [loading, setLoading] = useState<boolean>(false)

    const findOrderBooking = (id: number) =>
        originalOrder?.bookings.find(booking => booking.id === id)

    const recalculateRefund = async (bookings: RefundBooking[]) => {
        if (!originalOrder?.id) return
        const req: RefundPaymentRequest = {
            bookings,
            type:
                (currentRefundWay?.value as PaymentMethodName) ??
                PaymentMethodName.Cash,
            is_full_refund: false,
            calculate_only: true,
        }
        setLoading(true)
        const resp = await PaymentApi.refundOrder(originalOrder.id, req)
        setLoading(false)

        const respTotal = Number(resp) || 0
        const validTotal = Math.min(totalAmountPaid, respTotal)
        setTotal(validTotal)
    }

    const recalculateNextRefund = async (bookings: RefundBooking[]) => {
        if (!originalOrder?.id) return
        const req: RefundPaymentRequest = {
            bookings,
            type:
                (currentRefundWay?.value as PaymentMethodName) ??
                PaymentMethodName.Cash,
            is_full_refund: false,
            calculate_only: true,
        }
        setLoading(true)
        const resp = await PaymentApi.refundOrder(originalOrder.id, req)
        setLoading(false)
        return Number(resp)
    }

    const getDefaultBooking = (bookingId: number): RefundBooking | null => {
        const orderBooking = findOrderBooking(bookingId)
        if (!orderBooking) return null

        const fullAreaPrice = orderBooking.areaTotalPriceWithDiscount ?? 0
        const fullNomenclaturesPrice =
            orderBooking.nomenclaturePriceWithDiscount ?? 0
        const range = getBookingRangeDays(orderBooking)

        const count = fullAreaPrice <= availableMoney ? range : 1
        const nomenclatures =
            fullAreaPrice + fullNomenclaturesPrice <= availableMoney
                ? orderBooking.nomenclatures.map(n => ({
                      id: n.id,
                      count: n.count,
                  }))
                : []

        return {
            booking_id: bookingId,
            area_hours: { count },
            nomenclatures,
        }
    }

    const setBookingsAndRecalculate = async (bookings: RefundBooking[]) => {
        setSelectedBookings(bookings)
        await recalculateRefund(bookings)
    }

    const addBooking = async (bookingId: number) => {
        const newBooking = getDefaultBooking(bookingId)

        if (!newBooking) return

        await setBookingsAndRecalculate([
            ...selectedBookings.filter(
                b => b.booking_id !== newBooking.booking_id,
            ),
            newBooking,
        ])
    }

    const removeBooking = async (bookingId: number) => {
        await setBookingsAndRecalculate(
            selectedBookings.filter(b => b.booking_id !== bookingId),
        )
    }

    const onChangeBookingsToRefund = async (
        bookingId: number,
        checked: boolean,
    ) => {
        if (checked) {
            await addBooking(bookingId)
        } else {
            await removeBooking(bookingId)
        }
    }

    const onChangeBookingsAreaCountToRefund = async (
        bookingId: number,
        count: number,
    ) => {
        const orderBooking = findOrderBooking(bookingId)
        if (!orderBooking) return

        const range = getBookingRangeDays(orderBooking)
        const valid = Math.max(0, Math.min(count, range))

        if (valid === 0) return removeBooking(bookingId)

        const newBookings = selectedBookings.map(booking => {
            if (booking.booking_id !== bookingId) {
                return booking
            }

            return {
                ...booking,
                area_hours: { count },
            }
        })

        await setBookingsAndRecalculate(newBookings)

        // Check to disable input
        // TODO: refactor it
        const nextCount = count + 1
        const nextNewBookings = selectedBookings.map(booking => {
            if (booking.booking_id !== bookingId) {
                return booking
            }

            return {
                ...booking,
                area_hours: { count: nextCount },
            }
        })
        const totals = await recalculateNextRefund(nextNewBookings)
        setIsAreaDisabled(prev => ({
            ...prev,
            [nextCount]: totalAmountPaid < (totals ?? 0),
        }))
    }

    const isBookingChecked = (id: number) => {
        const sel = selectedBookings.find(b => b.booking_id === id)
        return (sel?.area_hours?.count ?? 0) > 0
    }

    const onChangeNomenclaturesCount = async (
        bookingId: number,
        nomenclatureId: number,
        count: number,
    ) => {
        const orderBooking = findOrderBooking(bookingId)
        if (!orderBooking) return

        const max =
            orderBooking.nomenclatures.find(n => n.id === nomenclatureId)
                ?.count ?? 0
        if (count > max || count < 0) return

        const updated = selectedBookings
            .map(b =>
                b.booking_id === bookingId
                    ? {
                          ...b,
                          nomenclatures: updateNomenclatures(
                              b.nomenclatures,
                              nomenclatureId,
                              count,
                          ),
                      }
                    : b,
            )
            .filter(b => b.nomenclatures?.length)

        if (!updated.some(b => b.booking_id === bookingId)) {
            updated.push({
                booking_id: bookingId,
                nomenclatures: [{ id: nomenclatureId, count }],
            })
        }

        await setBookingsAndRecalculate(updated)

        if (count + 1 > max) {
            setIsNomenclatureDisabled(prev => ({
                ...prev,
                [`${nomenclatureId}_${count + 1}`]: true,
            }))
            return
        }
        // Check to disable input
        // TODO: refactor it
        const nextNewBookings = selectedBookings
            .map(b =>
                b.booking_id === bookingId
                    ? {
                          ...b,
                          nomenclatures: updateNomenclatures(
                              b.nomenclatures,
                              nomenclatureId,
                              count + 1,
                          ),
                      }
                    : b,
            )
            .filter(b => b.nomenclatures?.length)
        const totals = await recalculateNextRefund(nextNewBookings)
        setIsNomenclatureDisabled(prev => ({
            ...prev,
            [`${nomenclatureId}_${count + 1}`]: totalAmountPaid < (totals ?? 0),
        }))
    }

    const updateNomenclatures = (
        current:
            | {
                  id: number
                  count: number
              }[]
            | undefined,
        id: number,
        count: number,
    ) =>
        count === 0
            ? (current ?? []).filter(n => n.id !== id)
            : (current ?? []).some(n => n.id === id)
              ? (current ?? []).map(n => (n.id === id ? { ...n, count } : n))
              : [...(current ?? []), { id, count }]

    const isNomenclatureChecked = (bookingId: number, nomenclatureId: number) =>
        selectedBookings
            .find(b => b.booking_id === bookingId)
            ?.nomenclatures?.some(
                n => n.id === nomenclatureId && n.count > 0,
            ) ?? false

    const getNomenclaturesCount = (bookingId: number, nomenclatureId: number) =>
        selectedBookings
            .find(b => b.booking_id === bookingId)
            ?.nomenclatures?.find(n => n.id === nomenclatureId)?.count ?? 0

    const getAreaCount = (bookingId: number) =>
        selectedBookings.find(b => b.booking_id === bookingId)?.area_hours
            ?.count ?? 0

    const submitRefund = async (full: boolean) => {
        if (!originalOrder?.id || !currentRefundWay?.value) return

        const isFull = full
        const req: RefundPaymentRequest = isFull
            ? {
                  is_full_refund: true,
                  type: currentRefundWay.value as PaymentMethodName,
                  refund_comment: refundComment,
                  delete_order_after_refund: false,
              }
            : {
                  bookings: selectedBookings,
                  type: currentRefundWay.value as PaymentMethodName,
                  is_full_refund: false,
                  delete_order_after_refund: false,
                  refund_comment: refundComment,
              }

        await PaymentApi.refundOrder(originalOrder.id, req)
        await updateBookings(originalOrder.id, undefined, true)
        setRefundComment('')
        handleCloseSidebar()
    }

    const handleCloseSidebar = () => {
        dispatch(clearBookingState())
        dispatch(closeSidebar())
    }

    return {
        totalPrice: total,
        isCompany,
        selectedBookings,
        originalOrder,
        currentRefundWay,

        setCurrentRefundWay,
        onChangeNomenclaturesCount,
        getNomenclaturesCount,
        isBookingChecked,
        isNomenclatureChecked,
        refund: () => submitRefund(false),
        fullRefund: () => submitRefund(true),
        onChangeBookingsToRefund,
        onChangeBookingsAreaCountToRefund,
        getAreaCount,

        isAreaDisabled,
        isNomenclatureDisabled,

        loading,

        refundComment,
        setRefundComment,
    }
}
